import { PrebidRequester } from './PrebidRequester.js';
import { Targeting } from './Targeting.js';
import type { CustomSettings } from './CustomSettings.js';
import type { ConsentManagement } from './ConsentManagement.js';
import type { Pbjs } from '../Pbjs.js';

export namespace BidderSpecific {
  export namespace Settings {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
    export function* get<TMap extends CustomSettings.Map>(
      bidderCodeToCustomSettingsMap: TMap
    ) {
      for (const [
        bidderCode,
        { revenueFactor }
      ] of bidderCodeToCustomSettingsMap) {
        if (!revenueFactor) continue;

        yield [
          bidderCode,
          {
            bidCpmAdjustment(bidCpm, bidResponse, _bidRequest) {
              return bidResponse?.netRevenue ? bidCpm : bidCpm * revenueFactor;
            }
          }
        ] as const satisfies readonly [
          bidderCode: string,
          bidderSettings: Pbjs.BidderSettings.Value
        ];
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
    export function apply<TMap extends CustomSettings.Map>(
      pbjs: PrebidRequester['pbjs'],
      bidderCodeToCustomSettingsMap: TMap
    ) {
      pbjs.que.push(() => {
        const bidderSettings = (pbjs.bidderSettings ??= {});

        for (const [bidderCode, bidderSpecificSettings] of get(
          bidderCodeToCustomSettingsMap
        )) {
          bidderSettings[bidderCode] = bidderSpecificSettings;
        }

        const standard = (bidderSettings.standard ??= {});
        standard.adserverTargeting ??= Array.from(Targeting.Mappers.get(pbjs));
        standard.storageAllowed = true;
      });
    }
  }

  export namespace Configs {
    export function apply(
      pbjs: PrebidRequester['pbjs'],
      customConsentVendorsMap: ConsentManagement.CustomVendors.Map,
      customSettingsMap: CustomSettings.Map
    ) {
      pbjs.que.push(() => {
        for (const [
          bidderCode,
          { schainNodes: nodes }
        ] of customConsentVendorsMap) {
          const sspSettings = customSettingsMap.get(bidderCode);
          const sspType = sspSettings?.sspType;
          const sellersJsonNode = nodes.find(
            (node) => node.asi && node.sid && node.hp
          );
          const schainNodes = sellersJsonNode ? [sellersJsonNode] : [];
          const nodesValue =
            sspType ===
            PrebidRequester.SiteData.SspCustomSettings.Value.SspTypes.RESELLER
              ? schainNodes
              : nodes;

          // TEMP LOGGING: Log ALL bidders before any filtering
          console.log(`[1. BidderSpecific] Processing ${bidderCode}:`, {
            sspType,
            directType:
              PrebidRequester.SiteData.SspCustomSettings.Value.SspTypes.DIRECT,
            isDirectSSP:
              sspType ===
              PrebidRequester.SiteData.SspCustomSettings.Value.SspTypes.DIRECT
          });
          // END TEMP LOGGING

          // For DIRECT SSPs: Skip schain configuration entirely (let SSP handle it)
          if (
            sspType ===
            PrebidRequester.SiteData.SspCustomSettings.Value.SspTypes.DIRECT
          ) {
            console.log(
              `[2. BidderSpecific] SKIPPING ${bidderCode} - DIRECT SSP`
            );
            continue;
          }

          // TEMPORARY LOGGING TO VERIFY THE LOGIC
          const shouldAddComplete =
            sspType !==
            PrebidRequester.SiteData.SspCustomSettings.Value.SspTypes.DIRECT;

          console.log(`[3. BidderSpecific] ${bidderCode}:`, {
            sspType,
            directType:
              PrebidRequester.SiteData.SspCustomSettings.Value.SspTypes.DIRECT,
            shouldAddComplete,
            nodesValue
          });
          // END TEMPORARY LOGGING

          pbjs.setBidderConfig({
            bidders: [bidderCode],
            config: {
              schain: {
                validation: 'relaxed',
                config: {
                  ver: '1.0',
                  nodes: nodesValue,
                  ...(shouldAddComplete && { complete: 1 })
                }
              }
            }
          });
        }
      });
    }
  }
}
